import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { getRecentCandidates } from '../utils/recentCandidates';
import { BriefcaseIcon, UserGroupIcon, ClockIcon } from '@heroicons/react/24/outline';
import WhatsAppButton from '../components/WhatsAppButton';
import CandidateStatsCards from '../components/CandidateStatsCards';

const SuperAdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [recentCandidates, setRecentCandidates] = useState([]);

  useEffect(() => {
    const fetchStats = async () => {
      try {
        const response = await axios.get('/api/dashboard/stats');
        setData(response.data);
      } catch (error) {
        console.error('Error fetching dashboard stats:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchStats();
    setRecentCandidates(getRecentCandidates(user?.id));
  }, [user]);

  if (loading) {
    return (
      <Layout>
        <div className="text-center py-12 text-gray-500">Loading...</div>
      </Layout>
    );
  }

  if (!data) {
    return (
      <Layout>
        <div className="text-center py-12 text-gray-500">Failed to load dashboard.</div>
      </Layout>
    );
  }

  const { liveJobs, recentJobs, candidateStats, byPosition, duplicateStats, newLeadsContactStats } = data;
  const recentJobsData = recentJobs || {
    total: liveJobs?.total || 0,
    jobs: (liveJobs?.jobs || []).map((job) => ({
      ...job,
      status: 'open',
      postedOn: job.liveSince
    }))
  };

  const postedSince = (dateStr) => {
    if (!dateStr) return '-';
    const diff = Math.floor((new Date() - new Date(dateStr)) / (1000 * 60 * 60 * 24));
    if (diff <= 0) return 'Today';
    if (diff === 1) return '1 day ago';
    return `${diff} days ago`;
  };

  const formatSpend = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const parsed = Number(value);
    if (Number.isNaN(parsed)) return value;
    return parsed.toLocaleString(undefined, {
      minimumFractionDigits: parsed % 1 === 0 ? 0 : 2,
      maximumFractionDigits: 2
    });
  };

  return (
    <Layout>
      <div className="px-4 pt-2 pb-6 sm:px-0">
        {/* Recent Jobs */}
        <div className="bg-white shadow rounded-lg overflow-hidden mb-6">
          <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
            <div className="flex items-center">
              <BriefcaseIcon className="h-5 w-5 text-green-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Recent Jobs</h2>
            </div>
            <span className="text-sm font-bold text-blue-700 bg-blue-100 px-3 py-1 rounded-full">
              {recentJobsData.total} total
            </span>
          </div>
          {recentJobsData.jobs.length > 0 ? (
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Site</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Candidates</th>
                  <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Posted On</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {recentJobsData.jobs.map((job) => (
                  <tr key={job.id} className="hover:bg-gray-50">
                    <td className="px-6 py-3 text-sm font-medium text-gray-900">{job.position}</td>
                    <td className="px-6 py-3 text-sm text-gray-700">
                      <div className="font-medium">
                        {job.url ? (
                          <a
                            href={job.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            {job.title}
                          </a>
                        ) : (
                          <span className="text-gray-800">{job.title}</span>
                        )}
                      </div>
                      <div className="mt-1 flex items-center gap-2 flex-wrap">
                        {job.status === 'open' ? (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800">Live</span>
                        ) : (
                          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-800">Closed</span>
                        )}
                        {job.status === 'closed' && (
                          <>
                            <span className="text-xs text-gray-500">Spend: {formatSpend(job.totalSpent)}</span>
                            {job.closedApplicantsScreenshotPath && (
                              <span className="relative group inline-flex items-center">
                                <span className="text-xs text-blue-600 cursor-default">Screenshot</span>
                                <span className="hidden group-hover:block absolute bottom-full left-1/2 -translate-x-1/2 mb-2 z-20 w-60 rounded-md border border-gray-200 bg-white p-2 shadow-xl">
                                  <img
                                    src={`/uploads/${job.closedApplicantsScreenshotPath}`}
                                    alt="Closed job screenshot preview"
                                    className="w-full h-auto rounded"
                                  />
                                </span>
                              </span>
                            )}
                          </>
                        )}
                      </div>
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500">{job.site || '-'}</td>
                    <td className="px-6 py-3 text-sm text-gray-900 text-right font-medium">
                      {job.status === 'closed' && job.closedApplicantsCount !== null && job.closedApplicantsCount !== undefined
                        ? job.closedApplicantsCount
                        : job.candidateCount}
                    </td>
                    <td className="px-6 py-3 text-sm text-gray-500 text-right">{postedSince(job.postedOn)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div className="px-6 py-4 text-sm text-gray-500 text-center">No recent jobs</div>
          )}
        </div>

        {/* Candidate Stats */}
        <CandidateStatsCards candidateStats={candidateStats} />

        {/* New Leads Contact Stats */}
        {newLeadsContactStats && (
          <div className="mb-6">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
              <div className="bg-white shadow rounded-lg p-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">New Leads Contacted (Today)</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{newLeadsContactStats.last12Hours}</div>
              </div>
              <div className="bg-white shadow rounded-lg p-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">New Leads Contacted (Last 3 Days)</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{newLeadsContactStats.last72Hours}</div>
                <div className="text-xs text-gray-500 mt-1">{newLeadsContactStats.last3DaysAvgPerDay} avg/day</div>
              </div>
              <div className="bg-white shadow rounded-lg p-4">
                <div className="text-xs font-medium text-gray-500 uppercase tracking-wider">New Leads Contacted (Last 7 Days)</div>
                <div className="text-2xl font-bold text-gray-900 mt-1">{newLeadsContactStats.last7DaysTotal}</div>
                <div className="text-xs text-gray-500 mt-1">{newLeadsContactStats.last7DaysAvgPerDay} avg/day</div>
              </div>
            </div>

            {/* Hourly Activity Heatmap */}
            {newLeadsContactStats.hourlyActivity && (() => {
              const activity = newLeadsContactStats.hourlyActivity;
              const slots = Array.from({ length: 24 }, (_, index) => {
                const totalMinutes = (11 * 60) + (index * 30);
                return {
                  hour: Math.floor(totalMinutes / 60),
                  minute: totalMinutes % 60
                };
              });
              // Build list of last 7 days (most recent first)
              const days = [];
              for (let i = 0; i < 7; i++) {
                const d = new Date();
                d.setDate(d.getDate() - i);
                days.push(d.toISOString().split('T')[0]);
              }
              // Build lookup: { 'YYYY-MM-DD': { hour: count } }
              const lookup = {};
              activity.forEach(({ day, hour, minute, count }) => {
                const dayStr = typeof day === 'string' ? day.split('T')[0] : new Date(day).toISOString().split('T')[0];
                if (!lookup[dayStr]) lookup[dayStr] = {};
                lookup[dayStr][`${hour}:${minute || 0}`] = count;
              });
              const maxCount = Math.max(1, ...activity.map(a => a.count));
              const getColor = (count) => {
                if (count === 0) return 'bg-gray-100';
                const intensity = count / maxCount;
                if (intensity <= 0.25) return 'bg-blue-200';
                if (intensity <= 0.5) return 'bg-blue-300';
                if (intensity <= 0.75) return 'bg-blue-500';
                return 'bg-blue-700';
              };
              const dayLabel = (dateStr) => {
                const d = new Date(dateStr + 'T00:00:00');
                return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
              };
              const timeLabel = (hour, minute, abbreviated = false) => {
                const suffix = hour >= 12 ? 'p' : 'a';
                const hour12 = hour % 12 || 12;
                if (abbreviated && minute === 0) {
                  return `${hour12}${suffix}`;
                }
                return `${hour12}:${minute === 0 ? '00' : '30'}${suffix}`;
              };

              return (
                <div className="bg-white shadow rounded-lg p-5">
                  <h3 className="text-sm font-medium text-gray-500 mb-3">Message Activity by 30-Minute Slot (11 AM-11 PM IST)</h3>
                  <div className="overflow-x-auto">
                    <div style={{ minWidth: '700px' }}>
                      {/* Hour labels */}
                      <div className="flex items-center mb-1">
                        <div className="w-28 flex-shrink-0" />
                        {slots.map(({ hour, minute }, index) => (
                          <div key={`${hour}-${minute}`} className="flex-1 text-center text-[10px] text-gray-400">
                            {index % 2 === 0 ? timeLabel(hour, minute, true) : ''}
                          </div>
                        ))}
                      </div>
                      {/* Rows */}
                      {days.map((dayStr) => (
                        <div key={dayStr} className="flex items-center mb-0.5">
                          <div className="w-28 flex-shrink-0 text-xs text-gray-500 pr-2 text-right truncate">
                            {dayLabel(dayStr)}
                          </div>
                          {slots.map(({ hour, minute }) => {
                            const slotKey = `${hour}:${minute}`;
                            const count = (lookup[dayStr] && lookup[dayStr][slotKey]) || 0;
                            return (
                              <div key={slotKey} className="flex-1 px-px" title={`${dayLabel(dayStr)} ${timeLabel(hour, minute)} — ${count} msg${count !== 1 ? 's' : ''} sent`}>
                                <div
                                  className={`w-full rounded-sm ${getColor(count)} transition-colors`}
                                  style={{ paddingBottom: '100%' }}
                                />
                              </div>
                            );
                          })}
                        </div>
                      ))}
                      {/* Legend */}
                      <div className="flex items-center justify-end mt-2 gap-1">
                        <span className="text-[10px] text-gray-400 mr-1">Less</span>
                        {['bg-gray-100', 'bg-blue-200', 'bg-blue-300', 'bg-blue-500', 'bg-blue-700'].map((c) => (
                          <div key={c} className={`w-3 h-3 rounded-sm ${c}`} />
                        ))}
                        <span className="text-[10px] text-gray-400 ml-1">More</span>
                      </div>
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        )}

        {/* Duplicate Stats */}
        {duplicateStats && (
          <div className="bg-white shadow rounded-lg p-5 mb-6">
            <div className="flex items-center mb-2">
              <UserGroupIcon className="h-5 w-5 text-yellow-500 mr-2" />
              <h3 className="text-sm font-medium text-gray-500">Duplicate Merge Statistics (This Month)</h3>
            </div>
            <p className="text-2xl font-bold text-gray-900">{duplicateStats.mergesThisMonth} duplicate{duplicateStats.mergesThisMonth !== 1 ? 's' : ''} merged</p>
            <p className="text-xs text-gray-500 mt-1">
              {duplicateStats.duplicatePercentage}% of recent imports/additions were duplicates ({duplicateStats.totalActivitiesThisMonth} total activities)
            </p>
          </div>
        )}

        {/* Side by side: Candidates by Position + Recent Candidates */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          {/* Candidates by Position */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center">
              <UserGroupIcon className="h-5 w-5 text-gray-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Candidates by Position</h2>
            </div>
            {byPosition.length > 0 ? (
              <table className="min-w-full table-fixed divide-y divide-gray-200">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="w-1/2 px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Position</th>
                    <th className="w-1/6 px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">7D</th>
                    <th className="w-1/6 px-3 py-3 text-right text-xs font-medium text-gray-500 uppercase">30D</th>
                    <th className="w-1/6 px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Total</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {byPosition.map((item, idx) => (
                    <tr key={idx} className="hover:bg-gray-50">
                      <td className="px-6 py-3 text-sm text-gray-900 truncate" title={item.position}>{item.position}</td>
                      <td className="px-3 py-3 text-sm text-right text-gray-700 font-medium">{item.last7Days}</td>
                      <td className="px-3 py-3 text-sm text-right text-gray-700 font-medium">{item.last30Days}</td>
                      <td className="px-6 py-3 text-sm text-right text-gray-900 font-semibold">{item.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            ) : (
              <div className="px-6 py-4 text-sm text-gray-500 text-center">No data yet</div>
            )}
          </div>

          {/* Last 10 Candidates Touched */}
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 flex items-center">
              <ClockIcon className="h-5 w-5 text-orange-500 mr-2" />
              <h2 className="text-lg font-semibold text-gray-900">Recently Touched</h2>
            </div>
            <div className="divide-y divide-gray-200">
              {recentCandidates.map((c) => (
                <div
                  key={c.id}
                  className="w-full px-6 py-3 flex items-center justify-between hover:bg-gray-50"
                >
                  <button
                    onClick={() => navigate(`/applicants/${c.id}`)}
                    className="flex items-center text-left flex-1 min-w-0"
                  >
                    <span className="text-sm font-medium text-blue-600">{c.name}</span>
                    <span className="text-xs text-gray-500 ml-2">{c.position || ''}</span>
                  </button>
                  <div className="flex items-center ml-2">
                    {c.phone && <WhatsAppButton countryCode={c.countryCode} phone={c.phone} />}
                    <span className="text-xs text-gray-400 ml-2">
                      {c.touchedAt ? new Date(c.touchedAt).toLocaleDateString() : ''}
                    </span>
                  </div>
                </div>
              ))}
              {recentCandidates.length === 0 && (
                <div className="px-6 py-4 text-sm text-gray-500 text-center">No recent activity — click on a candidate to start tracking</div>
              )}
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SuperAdminDashboard;
